import { Loader2 } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Input from "./Input";
import inputStyles from "./Input.module.scss";
import styles from "./MultiSelectDropdownInput.module.scss";

import Icons from "@/components/icons";
import Tag from "@/components/ui/v1/tag/Tag";
import { useInfiniteScroll } from "@/lib/hooks/useInfiniteScroll";

export interface DropdownOption<T> {
  count?: number | string;
  disabled?: boolean;
  label?: string;
  value: T;
}

interface MultiSelectDropdownProps<T> {
  disable?: boolean;
  disabledOptions?: T[];
  hasMore?: boolean;
  isFetching?: boolean;
  isFetchingNextPage?: boolean;
  isLoading?: boolean;
  mode?: "single" | "multiple";
  name: string;
  onChange?: (value: DropdownOption<T>[] | DropdownOption<T> | null) => void;
  onFetchMore?: () => void;
  onSearch?: (value: string) => void;
  options: DropdownOption<T>[];
  placeholder?: string;
  postfix?: React.ReactNode;
  readOnly?: boolean;
  searchable?: boolean;
  value: DropdownOption<T>[] | DropdownOption<T> | null;
  size?: "small" | "medium" | "large";
}

const MultiSelectDropdownInput = <T extends string | number>({
  disable = false,
  disabledOptions = [],
  hasMore = false,
  isFetching = false,
  isFetchingNextPage = false,
  isLoading,
  mode = "multiple",
  name,
  onChange,
  onFetchMore,
  onSearch,
  options = [],
  placeholder,
  postfix,
  readOnly = false,
  searchable = false,
  size = "medium",
  value = [],
}: MultiSelectDropdownProps<T>) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState<
    DropdownOption<T>[] | DropdownOption<T> | null
  >(value);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isSearching, setIsSearching] = useState(false);
  const [opensUpward, setOpensUpward] = useState(false);
  const wasOpened = React.useRef(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const optionListRef = React.useRef<HTMLDivElement>(null);
  const { handleScroll } = useInfiniteScroll({
    onFetchMore,
    hasMore,
    isLoading,
    isFetching: isFetchingNextPage,
  });

  useEffect(() => {
    if (Array.isArray(selected) || selected === null) {
      return;
    }

    const selectedOption = options.find(
      (option) => option.value === selected.value
    );

    if (selectedOption?.disabled) {
      const newSelected =
        options.filter((option) => !option.disabled)[0] ?? null;
      setSelected(newSelected);
      onChange?.(newSelected);
    }
  }, [options, selected, onChange]);

  useEffect(() => {
    setSelected(value);
  }, [value]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setQuery(value);
      onSearch?.(value);
    },
    [onSearch]
  );

  const filteredOptions =
    searchable && !onSearch
      ? options.filter((option) =>
        option.label?.toLowerCase().includes(query.toLowerCase())
      )
      : options;

  const calculateDropdownPosition = useCallback(() => {
    if (!dropdownRef.current) return;

    const rect = dropdownRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dropdownHeight = 200; // Max height from CSS
    const margin = 20; // Buffer space

    // Check if there's enough space below
    const spaceBelow = viewportHeight - rect.bottom - margin;
    const spaceAbove = rect.top - margin;

    // If there's not enough space below but enough space above, open upwards
    const shouldOpenUpward =
      spaceBelow < dropdownHeight && spaceAbove >= dropdownHeight;
    setOpensUpward(shouldOpenUpward);
  }, []);

  const toggleDropdown = () => {
    if (isLoading) return;

    if (!isOpen) {
      // Calculate position before opening
      calculateDropdownPosition();
    }

    setIsOpen((prev) => !prev);
  };

  const handleOptionClick = (option: DropdownOption<T>) => {
    if (disable || disabledOptions.includes(option.value) || option.disabled)
      return;

    if (mode === "multiple" && Array.isArray(selected)) {
      const newSelected = selected.some(({ value }) => value === option.value)
        ? selected.filter(({ value }) => value !== option.value)
        : [...selected, option];

      setSelected(newSelected);
      onChange?.(newSelected);
    } else {
      setSelected(option);
      onChange?.(option);
      setIsOpen(false);
    }

    if (searchable) {
      handleSearchChange("");
    }
  };

  useEffect(() => {
    if (isOpen) {
      wasOpened.current = true;
      // Recalculate position when opening
      calculateDropdownPosition();
      if (mode === "single" && searchable) {
        setIsSearching(true);
        setTimeout(() => inputRef.current?.focus(), 0);
      }
    } else if (wasOpened.current) {
      if (mode === "single" && searchable) {
        setIsSearching(false);
      }
      handleSearchChange("");
    }
  }, [isOpen, mode, searchable, handleSearchChange, calculateDropdownPosition]);

  useEffect(() => {
    const el = dropdownRef.current;
    if (!el) return;
    const handleFocusOut = (e: FocusEvent) => {
      if (!el.contains(e.relatedTarget as Node)) {
        setIsOpen(false);
        if (searchable) handleSearchChange("");
      }
    };
    el.addEventListener("focusout", handleFocusOut);
    return () => el.removeEventListener("focusout", handleFocusOut);
  }, [searchable, handleSearchChange]);

  const handleRemoveOption = (
    opt: DropdownOption<T>,
    e?: React.MouseEvent<SVGSVGElement>
  ) => {
    if (e) {
      e.stopPropagation();
    }

    if (mode === "multiple") {
      handleOptionClick(opt);
    }
  };

  const isSelected = (option: DropdownOption<T>) => {
    if (mode === "multiple" && Array.isArray(selected)) {
      return selected.some((opt) => opt.value === option.value);
    }
    return (selected as DropdownOption<T>)?.value === option.value;
  };

  useEffect(() => {
    if (isOpen && activeIndex >= 0) {
      const optionElement = optionListRef.current?.children[
        activeIndex
      ] as HTMLLIElement;
      optionElement?.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex, isOpen]);

  useEffect(() => {
    setActiveIndex(-1);
  }, [query]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!dropdownRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
        if (searchable) {
          handleSearchChange("");
        }
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [searchable, handleSearchChange]);

  useEffect(() => {
    const handleResize = () => {
      if (isOpen) {
        calculateDropdownPosition();
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isOpen, calculateDropdownPosition]);

  const dropdownContent = (
    <div
      style={{
        display: "flex",
        gap: "8px",
        width: "100%",
        flexWrap: "wrap",
        alignItems: "center",
        cursor: searchable ? "text" : undefined,
      }}
      role="presentation"
      onClick={() => {
        if (searchable) {
          inputRef.current?.focus();
        }
      }}
    >
      {mode === "multiple" &&
        Array.isArray(selected) &&
        selected.map((opt) => (
          <Tag
            key={opt.value}
            value={opt.label ?? opt.value + ""}
            icon={
              <Icons.CrossIcon onClick={(e) => handleRemoveOption(opt, e)} />
            }
          />
        ))}

      {searchable &&
      (mode === "multiple" || (mode === "single" && isSearching)) ? (
          <div style={{ display: "flex", width: "4px", flexGrow: 1 }}>
            <input
              ref={inputRef}
              value={query}
              placeholder={
                mode === "single" && selected && !Array.isArray(selected)
                  ? selected.label ?? ""
                  : mode === "multiple" &&
                  Array.isArray(selected) &&
                  selected.length > 0
                    ? ""
                    : placeholder
              }
              onKeyDown={(e) => {
                if (
                  e.key === "Backspace" &&
                query === "" &&
                mode === "multiple" &&
                Array.isArray(selected) &&
                selected.length > 0
                ) {
                  handleRemoveOption(selected[selected.length - 1]);
                } else if (e.key === "ArrowDown") {
                  e.preventDefault();
                  if (!isOpen) setIsOpen(true);
                  if (filteredOptions.length > 0) {
                    setActiveIndex((prev) => (prev + 1) % filteredOptions.length);
                  }
                } else if (e.key === "ArrowUp") {
                  e.preventDefault();
                  if (isOpen && filteredOptions.length > 0) {
                    setActiveIndex(
                      (prev) =>
                        (prev - 1 + filteredOptions.length) %
                      filteredOptions.length
                    );
                  }
                } else if (e.key === "Enter" && activeIndex > -1) {
                  e.preventDefault();
                  if (filteredOptions[activeIndex]) {
                    handleOptionClick(filteredOptions[activeIndex]);
                  }
                } else if (e.key === "Escape") {
                  setIsOpen(false);
                }
              }}
              onChange={(e) => {
                handleSearchChange(e.target.value);
              }}
              style={{
                minWidth: "10px",
                background: "transparent",
                border: "none",
                outline: "none",
                width: "100%",
              }}
            />
          </div>
        ) : (
          mode === "single" && (
            <span className="text-base font-normal" style={{ opacity: selected ? 1 : 0.5 }}>
              {(selected as DropdownOption<T>)?.label ?? placeholder}
            </span>
          )
        )}
    </div>
  );

  const optionsList = (
    <div
      id={`${name}-options`}
      className={`${styles.optionsList} ${
        opensUpward ? styles.optionsListUpward : ""
      }`}
      role="listbox"
    >
      <div className={styles.scroll} onScroll={handleScroll} ref={optionListRef}>
        {filteredOptions.map((option, index) => (
          <div
            key={option.value}
            onClick={() => {
              handleOptionClick(option);
              inputRef.current?.focus();
            }}
            onMouseDown={(e) => e.preventDefault()}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleOptionClick(option); inputRef.current?.focus(); } }}
            className={`
              ${styles.optionBase}
              ${styles.option}
              ${isSelected(option) ? styles.selected : ""}
              ${activeIndex === index ? styles.active : ""}
              ${
          disabledOptions.includes(option.value) || option.disabled
            ? styles.disabledOption
            : ""
          }`}
            role="option"
            tabIndex={-1}
            aria-selected={isSelected(option)}
            onMouseEnter={() => setActiveIndex(index)}
          >
            <span className="text-base font-normal">
              {option.label}
            </span>
            {option.count !== undefined && (
              <span className={styles.count}>({option.count})</span>
            )}
          </div>
        ))}
        {isFetchingNextPage && (
          <div className={styles.loadingContainer} style={{ padding: "8px" }}>
            <Loader2 className="size-6 animate-spin text-muted-foreground" />
          </div>
        )}
      </div>
    </div>
  );

  const emptyState = (
    <div
      id={`${name}-options`}
      className={`${styles.optionsList} ${
        opensUpward ? styles.optionsListUpward : ""
      }`}
    >
      {isFetching ? (
        <div className={styles.scroll}>
          <div className={styles.loadingContainer} style={{ padding: "12px" }}>
            <Loader2 className="size-6 animate-spin text-muted-foreground" />
          </div>
        </div>
      ) : (
        <div
          className={`${styles.optionBase} ${styles.option} ${styles.disabledOption}`}
          style={{ justifyContent: "center" }}
        >
          <span className="text-base font-normal">
            {t("commonSentences.noOptionsFound")}
          </span>
        </div>
      )}
    </div>
  );

  if (readOnly) {
    const displayValue = (() => {
      if (mode === "multiple" && Array.isArray(selected)) {
        return selected.map((opt) => opt.label ?? opt.value).join(", ");
      } else if (mode === "single" && selected && !Array.isArray(selected)) {
        return selected.label ?? selected.value;
      }
      return placeholder || "";
    })();

    return <Input readOnly={readOnly} value={displayValue} />;
  }

  return (
    <div
      className={`${inputStyles.inputWrapper} ${styles.inputWrapper} ${styles[size]}`}
      ref={dropdownRef}
      style={{ alignItems: "stretch" }}
    >
      {isLoading ? (
        <div
          className={styles.loadingContainer}
          style={{ justifyContent: "flex-end" }}
        >
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          <div
            className={styles.dropdown}
            onClick={searchable && isOpen ? undefined : toggleDropdown}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); if (!(searchable && isOpen)) toggleDropdown(); } }}
            role="combobox"
            tabIndex={0}
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            aria-controls={`${name}-options`}
            style={{ alignItems: "stretch" }}
          >
            <>{isLoading ? <Loader2 className="size-6 animate-spin text-muted-foreground" /> : dropdownContent}</>
          </div>
          {postfix && (
            <div
              className={styles.postfixIcon}
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                toggleDropdown();
              }}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); toggleDropdown(); } }}
            >
              {postfix}
            </div>
          )}
          {isOpen && filteredOptions.length > 0 && optionsList}
          {isOpen && filteredOptions.length === 0 && emptyState}
        </>
      )}
    </div>
  );
};

MultiSelectDropdownInput.displayName = "MultiSelectDropdownInput";

export default MultiSelectDropdownInput;
